import { z } from "zod";

import {
  CustomFieldRecord,
  CustomFieldType,
  UpdateCustomFieldRequest,
} from "@/shared/domain/customFields";

import {
  addEnumOptionIssues,
  collapseEnumValueWhitespace,
} from "./createCustomFieldForm";
import { descriptionSchema, displayNameSchema } from "./customFieldFormShared";

const editCustomFieldFormObjectSchema = z.object({
  display_name: displayNameSchema,
  description: descriptionSchema,
  // Options being added to an enum field, one entry per editor row. Existing options aren't
  // form values: they can't be edited or removed.
  enum_values: z.array(z.object({ value: z.string() })),
});

/** Inferred from the schema so the form's value type can't drift from validation. */
export type EditCustomFieldFormValues = z.infer<
  typeof editCustomFieldFormObjectSchema
>;

/**
 * Builds the edit-form validation schema for `field`. It is field-aware so new enum options
 * are checked against the options the field already has.
 */
export function makeEditCustomFieldFormSchema(field: CustomFieldRecord | null) {
  return editCustomFieldFormObjectSchema.superRefine((values, ctx) => {
    if (field?.options?.field_type === CustomFieldType.Enum) {
      addEnumOptionIssues(values.enum_values, ctx, field.options.enum_values);
    }
  });
}

/** Seed the form from the field's current values (null shows as an empty input). */
export function editFormDefaults(
  field: CustomFieldRecord,
): EditCustomFieldFormValues {
  return {
    display_name: field.display_name ?? "",
    description: field.description ?? "",
    enum_values: [],
  };
}

/** Trimmed value, or `null` when empty/whitespace-only (clears the attribute). */
function normalize(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Builds the update request from the form, including only the fields that
 * actually changed (others are omitted = left unchanged). An emptied/whitespace
 * field becomes `null` (cleared). Returns `{}` when nothing changed after trim.
 *
 * New enum options are sent after every existing one, because the server replaces the whole
 * list and rejects any update that leaves an existing option out.
 */
export function buildUpdateCustomFieldRequest(
  values: EditCustomFieldFormValues,
  original: CustomFieldRecord,
): UpdateCustomFieldRequest {
  const request: UpdateCustomFieldRequest = {};

  const nextDisplayName = normalize(values.display_name);
  if (nextDisplayName !== (original.display_name ?? null)) {
    request.display_name = nextDisplayName;
  }

  const nextDescription = normalize(values.description);
  if (nextDescription !== (original.description ?? null)) {
    request.description = nextDescription;
  }

  if (original.options?.field_type === CustomFieldType.Enum) {
    const addedValues = values.enum_values
      .map((option) => collapseEnumValueWhitespace(option.value))
      .filter((value) => value.length > 0);
    if (addedValues.length > 0) {
      request.options = {
        field_type: CustomFieldType.Enum,
        enum_values: [...original.options.enum_values, ...addedValues],
      };
    }
  }

  return request;
}
